import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TronEnergySummary } from '@/api';
import { formatCompactNumber, formatUsdt, USDT_SIGNIFICANT_DIGITS } from '@/utils/money';
import {
  ApiDocsGraphic,
  ChevronRightIcon,
  DollarBadgeIcon,
  ZapBadgeIcon,
} from '@/app/components/tron-energy/TronEnergyIcons';

export interface TronEnergySummaryCardsProps {
  summary?: TronEnergySummary | null;
  isLoading?: boolean;
  onDocsClick?: () => void;
}

function StatValue({
  value,
  unit,
  isLoading,
}: {
  value: string;
  unit?: string;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <div className="h-5 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />;
  }

  return (
    <div className="flex items-center gap-[5px] whitespace-nowrap">
      <p className="text-[20px] font-bold leading-5 text-slate-900 dark:text-white">{value}</p>
      {unit ? (
        <p className="text-sm font-semibold leading-5 text-emerald-600 dark:text-primary">{unit}</p>
      ) : null}
    </div>
  );
}

function DualStatCard({
  icon,
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
  rightUnit,
  isLoading,
  periodLabel,
}: {
  icon: ReactNode;
  leftLabel: string;
  leftValue: string;
  rightLabel: string;
  rightValue: string;
  rightUnit?: string;
  isLoading?: boolean;
  periodLabel: string;
}) {
  return (
    <div className="relative flex w-full shrink-0 flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-[#D2F0E1]/5 dark:bg-[#141517]">
      {icon}
      <div className="flex w-full items-start gap-5">
        <div className="flex min-w-px flex-1 flex-col items-start gap-1">
          <p className="w-full text-sm leading-5 text-slate-500 dark:text-slate-400">{leftLabel}</p>
          <StatValue value={leftValue} isLoading={isLoading} />
        </div>
        <div className="flex min-w-px flex-1 flex-col items-start gap-1">
          <p className="w-full text-sm leading-5 text-slate-500 dark:text-slate-400">{rightLabel}</p>
          <StatValue value={rightValue} unit={rightUnit} isLoading={isLoading} />
        </div>
      </div>
      <div className="absolute top-2 right-2 flex items-center rounded-[10px] bg-slate-100 px-2 py-1 dark:bg-[#D2F0E1]/5">
        <p className="text-xs leading-4 font-medium whitespace-nowrap text-slate-500 dark:text-slate-400">
          {periodLabel}
        </p>
      </div>
    </div>
  );
}

export function TronEnergySummaryCards({
  summary,
  isLoading,
  onDocsClick,
}: TronEnergySummaryCardsProps) {
  const { t } = useTranslation();
  const periodLabel = t('tronEnergy.summary.thisMonth', 'This Month');

  return (
    <div className="flex h-full flex-col items-start gap-4">
      <DualStatCard
        icon={<ZapBadgeIcon />}
        leftLabel={t('tronEnergy.summary.remainingPoints', '剩余可用积分')}
        leftValue={formatCompactNumber(summary?.remainingPoints, { locale: 'en-US' })}
        rightLabel={t('tronEnergy.summary.accountUsdt', '账户 USDT 余额')}
        rightValue={formatUsdt(summary?.accountUsdtBalance, {
          significantDigits: USDT_SIGNIFICANT_DIGITS,
        })}
        rightUnit="USDT"
        isLoading={isLoading}
        periodLabel={periodLabel}
      />
      <DualStatCard
        icon={<DollarBadgeIcon />}
        leftLabel={t('tronEnergy.summary.monthSpentPoints', '本月消费积分')}
        leftValue={formatCompactNumber(summary?.monthSpentPoints, { locale: 'en-US' })}
        rightLabel={t('tronEnergy.summary.monthSpentUsdt', '本月消费金额')}
        rightValue={formatUsdt(summary?.monthSpentUsdt, {
          significantDigits: USDT_SIGNIFICANT_DIGITS,
        })}
        rightUnit="USDT"
        isLoading={isLoading}
        periodLabel={periodLabel}
      />
      <DualStatCard
        icon={<ZapBadgeIcon />}
        leftLabel={t('tronEnergy.summary.monthRentedEnergy', '本月租用能量')}
        leftValue={formatCompactNumber(summary?.monthRentedEnergy, { locale: 'en-US' })}
        rightLabel={t('tronEnergy.summary.monthOrderCount', '本月订单数')}
        rightValue={formatCompactNumber(summary?.monthOrderCount, { locale: 'en-US' })}
        isLoading={isLoading}
        periodLabel={periodLabel}
      />

      <button
        type="button"
        onClick={onDocsClick}
        className="relative flex min-h-[145px] w-full flex-1 items-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-6 text-left dark:border-[#D2F0E1]/5 dark:bg-[#141517]"
      >
        <div className="relative z-10 flex flex-col items-start gap-3">
          <div className="flex items-center gap-1">
            <p className="text-[36px] leading-none font-bold whitespace-nowrap text-slate-900 dark:text-white">
              {t('tronEnergy.docs.button', 'API 文档')}
            </p>
            <ChevronRightIcon className="size-5 shrink-0 text-slate-900 dark:text-white" />
          </div>
          <p className="text-base leading-5 font-medium whitespace-nowrap text-slate-500 dark:text-slate-400">
            {t('tronEnergy.docs.goToView', 'Go to view')}
          </p>
        </div>
        <ApiDocsGraphic className="pointer-events-none absolute top-1/2 right-7 h-20 w-[79px] -translate-y-1/2" />
      </button>
    </div>
  );
}
